import React, { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import classes from "./DateTimeFieldRow.scss";

/**
 * A reusable row component for displaying date/time fields with labels and optional info tooltips.
 * Uses render props pattern to allow flexible content rendering for the value section.
 *
 * @component
 * @example
 * <DateTimeFieldRow
 *   label="Open date"
 *   tooltipText="Shows when the assignment is open for submission"
 *   renderValue={() => <div>Date and Time content</div>}
 * />
 */
const DateTimeFieldRow = ({
  label,
  tooltipText,
  renderValue,
  testId,
  className,
}) => {
  const rowContainerClasses = classNames(classes.rowContainer, className);

  return (
    <div className={rowContainerClasses} data-test-id={testId}>
      {/* Label section with optional tooltip */}
      <div className={classes.labelSection}>
        <div className={classNames("text-label", classes.labelText)}>
          {label}
        </div>
        {tooltipText ? (
          <Tooltip
            tooltip={tooltipText}
            dsVersion="2.0"
            showArrow={true}
            placement="right"
          >
            <div className={classes.infoIcon}>
              <InformationOutlined size="xxx-small" variant="subtle" />
            </div>
          </Tooltip>
        ) : null}
      </div>

      {/* Value section - uses render props for flexibility */}
      <div className={classes.valueSection}>{renderValue()}</div>
    </div>
  );
};

DateTimeFieldRow.propTypes = {
  /** Label text to display on the left side */
  label: PropTypes.string.isRequired,
  /** Optional tooltip text to show next to the label */
  tooltipText: PropTypes.string,
  /** Render prop function that returns the value content (date/time pickers, text, etc.) */
  renderValue: PropTypes.func.isRequired,
  /** Optional test ID for testing purposes */
  testId: PropTypes.string,
  /** Optional additional CSS class name */
  className: PropTypes.string,
};

DateTimeFieldRow.defaultProps = {
  tooltipText: "",
  testId: "",
  className: "",
};

export default memo(DateTimeFieldRow);
